import { useState } from 'react';
import { Plus, Trash, CaretDown, CaretRight, Warning, Info, FloppyDisk, Check, X, PencilSimple, ArrowsMerge } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useCategories } from '../../hooks/useCategories';
import { dashboardService } from '../../../../services/dashboard.service';
import type { PendingDetail } from '../../../../services/dashboard.service';

// Client-side row ids only — never sent to the server. Lets add/remove/rename
// work on stable React keys instead of the (editable) category name.
let nextId = 0;
const makeId = () => `row-${nextId++}`;

interface SubDraft {
  id: string;
  name: string;
  /** null for a row added in this session and never saved. */
  originalName: string | null;
  pending: boolean;
}

interface CategoryDraft {
  id: string;
  name: string;
  originalName: string | null;
  pending: boolean;
  subcategories: SubDraft[];
}

const buildDrafts = (
  order: string[],
  categoryMap: Record<string, string[]>,
  isPending: (category: string, sub?: string) => boolean,
): CategoryDraft[] =>
  order.map((name) => ({
    id: makeId(),
    name,
    originalName: name,
    pending: isPending(name),
    subcategories: (categoryMap[name] || []).map((sub) => ({
      id: makeId(),
      name: sub,
      originalName: sub,
      pending: isPending(name, sub),
    })),
  }));

/** True once a saved value has been edited to something else. */
const isRenamed = (draft: { name: string; originalName: string | null }) =>
  draft.originalName !== null && draft.name.trim() !== draft.originalName;

/** Key identifying one pending row — a category name, or "category::sub". */
const pendingKey = (p: { category: string; sub_category: string | null }) =>
  p.sub_category ? `${p.category}::${p.sub_category}` : p.category;

type PendingAction = 'approve' | 'rename' | 'merge' | 'reject';

const PendingRow = ({
  entry,
  onResolve,
}: {
  entry: PendingDetail;
  onResolve: (action: PendingAction, newName?: string) => Promise<void>;
}) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<'rename' | 'merge' | null>(null);
  const [targetName, setTargetName] = useState('');

  const run = async (action: PendingAction, newName?: string) => {
    setBusy(true);
    try {
      await onResolve(action, newName);
      setEditing(null);
      setTargetName('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-100 rounded-xl px-4 py-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900 truncate">
            {entry.sub_category || entry.category}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {entry.sub_category ? `Under "${entry.category}"` : 'New category'}
          </p>
          <p className="text-[11px] text-slate-400 mt-1">
            Added by {entry.added_by || 'unknown'}
            {entry.from_project ? ` · from project ${entry.from_project}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => void run('approve')}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 rounded-lg transition-smooth cursor-pointer"
            title="Approve — offer this to the AI"
          >
            <Check size={13} weight="bold" />
            Approve
          </button>
          <button
            onClick={() => setEditing(editing === 'rename' ? null : 'rename')}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 disabled:opacity-40 rounded-lg transition-smooth cursor-pointer"
            title="Rename and approve"
          >
            <PencilSimple size={13} weight="bold" />
            Rename
          </button>
          <button
            onClick={() => setEditing(editing === 'merge' ? null : 'merge')}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 disabled:opacity-40 rounded-lg transition-smooth cursor-pointer"
            title="Merge into an existing value"
          >
            <ArrowsMerge size={13} weight="bold" />
            Merge
          </button>
          <button
            onClick={() => void run('reject')}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 disabled:opacity-40 rounded-lg transition-smooth cursor-pointer"
            title="Reject — remove this pending value"
          >
            <X size={13} weight="bold" />
            Reject
          </button>
        </div>
      </div>

      {editing && (
        <div className="flex items-center gap-2 pl-1">
          <input
            autoFocus
            value={targetName}
            onChange={(e) => setTargetName(e.target.value)}
            placeholder={editing === 'rename' ? 'New name…' : 'Existing value to merge into…'}
            className="flex-1 bg-slate-50 border border-dashed border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-lg px-2.5 py-1.5 text-xs outline-none transition-smooth"
          />
          <button
            onClick={() => void run(editing, targetName)}
            disabled={busy || !targetName.trim()}
            className="text-xs font-bold text-accent hover:text-accent-hover disabled:opacity-40 disabled:cursor-not-allowed px-2 shrink-0 cursor-pointer"
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
};

export const CategoriesTab = () => {
  const { categoryMap, order, isPending, pendingDetail, loading, refresh } = useCategories();

  // Re-derive the editable draft whenever the canonical list changes: on the
  // first load, and after a successful save triggers refresh(). Adjusted
  // during render (React's recommended pattern for resetting state when an
  // input changes) rather than in an effect, so it doesn't cost an extra
  // commit-then-effect render pass.
  const [prevOrder, setPrevOrder] = useState(order);
  const [prevCategoryMap, setPrevCategoryMap] = useState(categoryMap);
  const [drafts, setDrafts] = useState<CategoryDraft[]>(() => buildDrafts(order, categoryMap, isPending));
  if (order !== prevOrder || categoryMap !== prevCategoryMap) {
    setPrevOrder(order);
    setPrevCategoryMap(categoryMap);
    setDrafts(buildDrafts(order, categoryMap, isPending));
  }

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubName, setNewSubName] = useState<Record<string, string>>({});

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const id = makeId();
    setDrafts((prev) => [...prev, { id, name, originalName: null, pending: false, subcategories: [] }]);
    setExpanded((prev) => new Set(prev).add(id));
    setNewCategoryName('');
  };

  const renameCategory = (id: string, value: string) => {
    setDrafts((prev) => prev.map((c) => (c.id === id ? { ...c, name: value } : c)));
  };

  const removeCategory = (id: string) => {
    setDrafts((prev) => prev.filter((c) => c.id !== id));
  };

  const addSub = (catId: string) => {
    const name = (newSubName[catId] || '').trim();
    if (!name) return;
    setDrafts((prev) =>
      prev.map((c) =>
        c.id === catId
          ? { ...c, subcategories: [...c.subcategories, { id: makeId(), name, originalName: null, pending: false }] }
          : c
      )
    );
    setNewSubName((prev) => ({ ...prev, [catId]: '' }));
  };

  const renameSub = (catId: string, subId: string, value: string) => {
    setDrafts((prev) =>
      prev.map((c) =>
        c.id === catId
          ? { ...c, subcategories: c.subcategories.map((s) => (s.id === subId ? { ...s, name: value } : s)) }
          : c
      )
    );
  };

  const removeSub = (catId: string, subId: string) => {
    setDrafts((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, subcategories: c.subcategories.filter((s) => s.id !== subId) } : c))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dashboardService.replaceCategories({
        categories: drafts.map((c, idx) => ({
          name: c.name.trim(),
          order: idx + 1,
          pending: c.pending,
          subcategories: c.subcategories.map((s) => ({
            name: s.name.trim(),
            pending: s.pending,
          })),
        })),
      });
      toast.success('Category list saved.');
      await refresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save the category list.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Pending ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Info size={18} className="text-accent" />
          Pending
          {pendingDetail.length > 0 && (
            <span className="text-[10px] font-bold text-accent bg-accent/10 rounded-full px-2 py-0.5">
              {pendingDetail.length}
            </span>
          )}
        </h2>

        {pendingDetail.length === 0 ? (
          <div className="mt-3 px-4 py-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 text-center">
            <p className="text-xs font-medium text-slate-500 max-w-md mx-auto">
              Nothing waiting. Values added by reviewers while correcting a project appear here for approval.
            </p>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {pendingDetail.map((entry) => (
              <PendingRow
                key={pendingKey(entry)}
                entry={entry}
                onResolve={async (action, newName) => {
                  try {
                    await dashboardService.resolvePendingCategory({
                      category: entry.category,
                      sub_category: entry.sub_category,
                      action,
                      new_name: newName,
                    });
                    toast.success(
                      action === 'approve' ? 'Approved — now offered to the AI.'
                        : action === 'reject' ? 'Rejected.'
                        : action === 'rename' ? 'Renamed and approved.'
                        : 'Merged.'
                    );
                    await refresh();
                  } catch (err: any) {
                    toast.error(err.message || 'Failed to resolve this pending value.');
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Categories editor ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-slate-800">Categories</h2>
          <button
            onClick={handleSave}
            disabled={saving || loading || drafts.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-smooth"
          >
            <FloppyDisk size={16} weight="bold" />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-48 text-slate-400 text-sm">Loading categories…</div>
        ) : (
          <div className="space-y-3">
            {drafts.map((c) => {
              const catRenamed = isRenamed(c);
              const isOpen = expanded.has(c.id);
              return (
                <div key={c.id} className="border border-slate-100 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-slate-50/50">
                    <button
                      onClick={() => toggleExpand(c.id)}
                      className="p-1 text-slate-400 hover:text-slate-700 shrink-0 cursor-pointer"
                      title={isOpen ? 'Collapse' : 'Expand'}
                    >
                      {isOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                    </button>
                    <input
                      value={c.name}
                      onChange={(e) => renameCategory(c.id, e.target.value)}
                      className="flex-1 bg-transparent font-bold text-sm text-slate-900 outline-none focus:bg-white focus:ring-2 focus:ring-accent/20 rounded-lg px-2 py-1 -mx-2 transition-smooth"
                    />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider shrink-0">
                      {c.subcategories.length} sub{c.subcategories.length === 1 ? '' : 's'}
                    </span>
                    <button
                      onClick={() => removeCategory(c.id)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors shrink-0 cursor-pointer"
                      title="Remove category"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>

                  {catRenamed && (
                    <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-start gap-2">
                      <Warning size={14} className="shrink-0 mt-0.5 text-amber-600" weight="fill" />
                      <span className="text-[11px] text-amber-700 font-medium">
                        Existing project records tagged "{c.originalName}" keep that value — renaming here does not rewrite them.
                      </span>
                    </div>
                  )}

                  {isOpen && (
                    <div className="px-4 py-3 space-y-1 border-t border-slate-100">
                      {c.subcategories.map((s) => {
                        const subRenamed = isRenamed(s);
                        return (
                          <div key={s.id}>
                            <div className="flex items-center gap-2 pl-6">
                              <input
                                value={s.name}
                                onChange={(e) => renameSub(c.id, s.id, e.target.value)}
                                className="flex-1 bg-transparent text-sm text-slate-700 outline-none focus:bg-slate-50 focus:ring-2 focus:ring-accent/20 rounded-lg px-2 py-1 -mx-2 transition-smooth"
                              />
                              <button
                                onClick={() => removeSub(c.id, s.id)}
                                className="p-1 text-slate-350 hover:text-rose-600 transition-colors shrink-0 cursor-pointer"
                                title="Remove sub-category"
                              >
                                <Trash size={12} weight="bold" />
                              </button>
                            </div>
                            {subRenamed && (
                              <div className="pl-6 pt-1 pb-1.5 flex items-start gap-1.5">
                                <Warning size={12} className="shrink-0 mt-0.5 text-amber-600" weight="fill" />
                                <span className="text-[10px] text-amber-700 font-medium">
                                  Records tagged "{s.originalName}" keep that value.
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Add sub-category */}
                      <div className="flex items-center gap-2 pl-6 pt-2">
                        <Plus size={12} className="text-slate-350 shrink-0" weight="bold" />
                        <input
                          value={newSubName[c.id] || ''}
                          onChange={(e) => setNewSubName((prev) => ({ ...prev, [c.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addSub(c.id);
                            }
                          }}
                          placeholder="Add sub-category…"
                          className="flex-1 bg-slate-50 border border-dashed border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-lg px-2.5 py-1.5 text-xs outline-none transition-smooth"
                        />
                        <button
                          onClick={() => addSub(c.id)}
                          disabled={!(newSubName[c.id] || '').trim()}
                          className="text-xs font-bold text-accent hover:text-accent-hover disabled:opacity-40 disabled:cursor-not-allowed px-2 shrink-0 cursor-pointer"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {drafts.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-500 font-medium">
                No categories yet — add one below.
              </div>
            )}

            {/* Add category */}
            <div className="flex items-center gap-2 pt-2">
              <Plus size={14} className="text-slate-400 shrink-0" weight="bold" />
              <input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCategory();
                  }
                }}
                placeholder="Add category…"
                className="flex-1 bg-slate-50 border border-dashed border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-xl px-3 py-2 text-sm outline-none transition-smooth"
              />
              <button
                onClick={addCategory}
                disabled={!newCategoryName.trim()}
                className="px-4 py-2 text-sm font-bold text-accent hover:text-accent-hover disabled:opacity-40 disabled:cursor-not-allowed shrink-0 cursor-pointer"
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
